import {
  useMutation,
  useQueryClient,
  type InvalidateQueryFilters,
  type QueryKey,
} from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices/DomainServicesContext";
import { datasetQueryKeys } from "@/shared/domain/datasets/hooks";
import { eventQueryKeys } from "@/shared/domain/events/hooks";
import { fileQueryKeys } from "@/shared/domain/files/hooks";
import { sessionQueryKeys } from "@/shared/domain/sessions/hooks";

import {
  CollectionRecord,
  CollectionResourceType,
  UpdateCollectionRequest,
} from "../CollectionRecord";

import { collectionQueryKeys } from "./collectionQueryKeys";

export interface UpdateCollectionVariables {
  collectionId: string;
  resourceOwnerId: string;
  updates: UpdateCollectionRequest;
}

/** Whether `value` is, or holds at any depth, the string `collectionId`. */
function namesCollection(value: unknown, collectionId: string): boolean {
  if (typeof value === "string") {
    return value === collectionId;
  }
  if (Array.isArray(value)) {
    return value.some((item) => namesCollection(item, collectionId));
  }
  if (typeof value === "object" && value !== null) {
    return Object.values(value).some((item) =>
      namesCollection(item, collectionId),
    );
  }
  return false;
}

/**
 * Matches the lists under `listsKey` whose query names the collection.
 *
 * For list keys that end in the submitted query body and have no collection
 * scope of their own. A list of a collection's members filters on the
 * collection's id, so the id appears in that body wherever the filter sits
 * among the user's own filters. Collection ids are globally unique, so the
 * id appearing at all identifies the list.
 */
function listsNamingCollection(
  listsKey: QueryKey,
  collectionId: string,
): InvalidateQueryFilters {
  return {
    queryKey: listsKey,
    predicate: (query) => namesCollection(query.queryKey, collectionId),
  };
}

/**
 * Which cached resource lists show a collection's members, keyed by the type
 * the collection holds.
 *
 * These match a list whether or not it is on screen. A collection changed
 * from another page has its cached member list marked stale here, which is
 * what makes that list refetch the next time the collection is opened.
 *
 * A `Record` over the union, so a fifth `CollectionResourceType` is a compile
 * error here. A lookup because `resource_type` arrives through an unchecked
 * response cast: an unrecognized value yields `undefined`.
 *
 * Match only the changed collection's lists. A filter that also matches the
 * lists of other collections makes every one of them refetch on each write,
 * and a test that checks only the changed collection's list still passes.
 */
const MEMBERSHIP_QUERY_FILTERS: Record<
  CollectionResourceType,
  (record: CollectionRecord) => InvalidateQueryFilters
> = {
  dataset: (record) =>
    listsNamingCollection(datasetQueryKeys.lists(), record.collection_id),
  file: (record) => ({
    queryKey: fileQueryKeys.listsScope(record.org_id, {
      type: "collection",
      id: record.collection_id,
    }),
  }),
  event: (record) => ({
    queryKey: eventQueryKeys.listsScope(record.org_id, {
      type: "collection",
      id: record.collection_id,
    }),
  }),
  session: (record) =>
    listsNamingCollection(sessionQueryKeys.lists(), record.collection_id),
};

/**
 * The single write path for an existing collection -- metadata edits and
 * membership changes alike.
 *
 * The cache maintenance is deliberately baked in rather than exposed as a
 * helper callers invoke themselves: a write that lands without its
 * invalidation leaves the UI showing stale membership, and nothing about the
 * call site makes that omission visible. Everything that edits a collection
 * goes through here, including the AI client tools, which receive
 * `mutateAsync` rather than the raw service.
 */
export function useUpdateCollection() {
  const { collections } = useDomainServices();
  const queryClient = useQueryClient();

  return useMutation<CollectionRecord, Error, UpdateCollectionVariables>({
    mutationFn: ({ collectionId, resourceOwnerId, updates }) =>
      collections.updateCollection(collectionId, updates, { resourceOwnerId }),
    onSuccess: (record) => {
      // The response is the newest state of the record, so it is written
      // straight into the detail cache rather than invalidated and refetched.
      queryClient.setQueryData(
        collectionQueryKeys.detail(record.collection_id),
        record,
      );
      void queryClient.invalidateQueries({
        queryKey: collectionQueryKeys.lists(record.org_id),
      });
      void queryClient.invalidateQueries({
        queryKey: collectionQueryKeys.changes(record.collection_id),
      });
      // Guarded because `invalidateQueries` with no filters invalidates every
      // query in the cache.
      const membershipFilters =
        MEMBERSHIP_QUERY_FILTERS[record.resource_type]?.(record);
      if (membershipFilters) {
        void queryClient.invalidateQueries(membershipFilters);
      }
    },
  });
}
